// Layout · Card
//
// Generic white surface: border + radius + optional titled section header.
// This is a layout primitive — it owns only the surface (background, border,
// radius, padding) and the optional header chrome. Data goes in via children.
//
// Props adopted from commons (tone, density, loading); visual styling:
// RADIUS.xl (12px), SURFACE.border, SURFACE.surface.
//
// Tone: applies a left-side stripe + tinted background for status-coded cards
// (info/warning/error/muted). Default tone has no stripe.
//
// Density controls internal padding: standard | dense | compact.
//
// Loading renders an AntD Skeleton.Button shimmer column so consumers don't
// need to lift their own loading state into the card surface.
//
// All visual values from @ds/tokens/palette. No hardcoded hex.

import React from "react";
import { Skeleton } from "antd";
import {
  FONT_SIZE,
  FONT_WEIGHT,
  LAYOUT,
  LETTER_SPACING,
  MOTION,
  RADIUS,
  SKELETON,
  SPACE,
  SURFACE,
  TEXT,
  TONE
} from "@ds/tokens/palette";

// ─── Off-scale card padding constants ────────────────────────────────────────
//
// These values don't belong on the SPACE scale (element gaps / icon offsets).
// They are container-level padding values tuned for the card surface specifically
// and are not shared with any other component — per Rule 1 review, they stay
// inlined here rather than living on the palette as a single-consumer block.

/** 18 px — off-scale, between SPACE.lg (16) and SPACE.xl (24); empirically
 *  tuned for card interior breathing room. Applies to body + header horizontal padding. */
const CARD_PAD_H = 18;

/** 14 px — reuses LAYOUT.headerStripPadY (14); coordinates with the entity-view
 *  header strip's vertical padding for the same editorial rhythm. */
const CARD_PAD_HEADER_V = LAYOUT.headerStripPadY;

// ─── Density ─────────────────────────────────────────────────────────────────

/**
 * Controls internal padding of the card body.
 * - standard: full breathing room (default)
 * - dense:    tighter vertical padding for compact card stacks
 * - compact:  minimal padding for flush/table contexts; prefer `flush` if you
 *             want zero padding
 */
export type CardDensity = "standard" | "dense" | "compact";

const BODY_PAD: Record<CardDensity, { v: number; h: number }> = {
  standard: { v: CARD_PAD_H, h: CARD_PAD_H },  // 18px × 18px
  dense:    { v: SPACE.md,   h: CARD_PAD_H },  // 12px × 18px
  compact:  { v: SPACE.sm,   h: SPACE.md   },  //  8px × 12px
};

// ─── Tone palette ─────────────────────────────────────────────────────────────
//
// Tone maps to a TONE ramp (info→blue, warning→amber, error→red, muted→grey).
// The stripe is 4 px wide on the left edge — same as the legacy commons Card.
// "default" has no stripe and uses the standard surface / border.

export type CardTone = "default" | "info" | "warning" | "error" | "muted";

const STRIPE_WIDTH = 4; // px — single-consumer, kept inline (not a palette token)

function toneStyle(tone: CardTone): { background: string; border: string; stripeColor: string | null } {
  switch (tone) {
    case "info":    return { background: TONE.blue.softBg,   border: TONE.blue.border,   stripeColor: TONE.blue.base   };
    case "warning": return { background: TONE.amber.softBg,  border: TONE.amber.border,  stripeColor: TONE.amber.base  };
    case "error":   return { background: TONE.red.softBg,    border: TONE.red.border,    stripeColor: TONE.red.base    };
    case "muted":   return { background: SURFACE.surfaceSoft, border: SURFACE.border,    stripeColor: null             };
    default:        return { background: SURFACE.surface,    border: SURFACE.border,     stripeColor: null             };
  }
}

// ─── Skeleton placeholder (loading state) ────────────────────────────────────

const CardSkeleton: React.FC = () => (
  <div style={{ display: "flex", flexDirection: "column", gap: SPACE.sm }}>
    {/* Simulate a header row */}
    <div style={{ display: "flex", alignItems: "center", gap: SPACE.sm }}>
      <Skeleton.Button active size="small" style={{ width: SKELETON.kvLabelW, height: SKELETON.kvLabelH }} />
      <Skeleton.Button active size="small" style={{ width: SKELETON.kvValueW, height: SKELETON.kvLabelH, flex: 1 }} />
    </div>
    {/* Simulate two value rows */}
    <div style={{ display: "flex", gap: SPACE.lg }}>
      <Skeleton.Button active size="small" style={{ width: SKELETON.kvValueW, height: SKELETON.kvValueH }} />
      <Skeleton.Button active size="small" style={{ width: SKELETON.kvValueW, height: SKELETON.kvValueH }} />
    </div>
    <div style={{ display: "flex", gap: SPACE.lg }}>
      <Skeleton.Button active size="small" style={{ width: SKELETON.kvLabelW, height: SKELETON.kvValueH }} />
      <Skeleton.Button active size="small" style={{ width: 100,               height: SKELETON.kvValueH }} />
    </div>
  </div>
);

// ─── Count badge (inline chip — mirrors design winner's Badge) ────────────────

const CountBadge: React.FC<{ count: number }> = ({ count }) => (
  <span
    style={{
      display:        "inline-flex",
      alignItems:     "center",
      justifyContent: "center",
      background:     SURFACE.surfaceHover,
      color:          TEXT.muted,
      fontSize:       FONT_SIZE.label,
      fontWeight:     FONT_WEIGHT.semibold,
      borderRadius:   RADIUS.pill,
      padding:        `0 ${SPACE.xs}px`,
      minWidth:       18,
      height:         18,
      lineHeight:     1,
    }}
  >
    {count}
  </span>
);

// ─── Inputs ───────────────────────────────────────────────────────────────────

export interface CardInput {
  /**
   * Section title. When set, renders a header row above a horizontal rule.
   * Use this for titled sub-section cards (form sections, sidebar panels).
   * Rendered as an uppercase eyebrow label (consistent with KVGrid labels).
   */
  title?: string;
  /**
   * Optional count badge rendered next to the title (e.g. line-item count).
   * Ignored when `title` is not set.
   */
  count?: number;
  /**
   * Right-anchored content in the title row (action buttons, status tags).
   * Ignored when `title` is not set.
   */
  right?: React.ReactNode;
  /**
   * When true, removes inner body padding (for full-bleed tables or lists).
   * Overrides `density` for the body slot only; the header still has its own padding.
   */
  flush?: boolean;
  /**
   * When true, removes ONLY vertical body padding (top/bottom). Horizontal
   * padding is preserved. Used for tables that bring their own row paddings
   * but should keep horizontal gutters aligned with the card.
   */
  flushY?: boolean;
  /** Tone: applies a left-side stripe + tinted background for status-coded cards. */
  tone?: CardTone;
  /**
   * Controls internal body padding.
   * standard (default) · dense · compact
   */
  density?: CardDensity;
  /**
   * When true, renders a skeleton shimmer instead of children.
   * Useful when the card's content is still loading.
   */
  loading?: boolean;
  /** data-testid applied to the root element. */
  testId?: string;
  /** DOM `id` on the root element — anchor target for scrollIntoView / hash links. */
  id?: string;
  /** Card body content. */
  children?: React.ReactNode;
  style?: React.CSSProperties;
  className?: string;
}

// Card has no Output events or Actions.
export type CardProps = CardInput;

// ─── Card ─────────────────────────────────────────────────────────────────────

export const Card: React.FC<CardProps> = ({
  title,
  count,
  right,
  flush = false,
  flushY = false,
  tone = "default",
  density = "standard",
  loading = false,
  testId,
  id,
  children,
  style,
  className,
}) => {
  const { background, border, stripeColor } = toneStyle(tone);
  const hasStripe = stripeColor !== null;
  const bodyPad = BODY_PAD[density];

  return (
    <div
      id={id}
      data-testid={testId}
      className={className}
      style={{
        position:     "relative",
        background,
        border:       `1px solid ${border}`,
        borderRadius: RADIUS.xl,
        overflow:     "hidden",
        transition:   `border-color ${MOTION.fast} ${MOTION.ease}`,
        ...style,
      }}
    >
      {/* Left-side tone stripe */}
      {hasStripe && (
        <div
          aria-hidden
          style={{
            position:   "absolute",
            left:       0,
            top:        0,
            bottom:     0,
            width:      STRIPE_WIDTH,
            background: stripeColor,
          }}
        />
      )}

      {/* Section header (title row) */}
      {title !== undefined && (
        <div
          data-testid="card-header"
          style={{
            display:        "flex",
            alignItems:     "center",
            justifyContent: "space-between",
            paddingTop:     CARD_PAD_HEADER_V,
            paddingBottom:  CARD_PAD_HEADER_V,
            paddingLeft:    hasStripe ? CARD_PAD_H + STRIPE_WIDTH : CARD_PAD_H,
            paddingRight:   CARD_PAD_H,
            borderBottom:   `1px solid ${SURFACE.border}`,
          }}
        >
          {/* Label + optional count badge */}
          <div
            style={{
              display:       "flex",
              alignItems:    "center",
              gap:           SPACE.sm,
              fontSize:      FONT_SIZE.label,
              fontWeight:    FONT_WEIGHT.semibold,
              letterSpacing: LETTER_SPACING.eyebrow,
              color:         TEXT.muted,
              textTransform: "uppercase",
              minWidth:      0,
            }}
          >
            {title}
            {count !== undefined && <CountBadge count={count} />}
          </div>
          {/* Right slot */}
          {right !== undefined && (
            <div data-testid="card-header-right" style={{ minWidth: 0, overflowX: "auto" }}>
              {right}
            </div>
          )}
        </div>
      )}

      {/* Body */}
      <div
        style={{
          paddingTop:    (flush || flushY) ? 0 : bodyPad.v,
          paddingBottom: (flush || flushY) ? 0 : bodyPad.v,
          paddingLeft:   flush ? 0 : (hasStripe ? bodyPad.h + STRIPE_WIDTH : bodyPad.h),
          paddingRight:  flush ? 0 : bodyPad.h,
          overflowX:     "auto",
          minWidth:      0,
        }}
      >
        {loading ? <CardSkeleton /> : children}
      </div>
    </div>
  );
};
